<template>  <AppModal
    :show="show"
    @close="$emit('close')"
    :title="$t('design.newDesign')"
    icon="plus"
    size="medium"
    :loading="saving"
  >
    <div class="design-form">
      <div class="design-form__group">
        <label for="designName" class="design-form__label">
          {{ $t('design.designName') }}
          <span class="design-form__required">*</span>
        </label>
        <input
          id="designName"
          v-model="formData.name"
          type="text"
          required
          class="design-form__input"
          :class="{ 'design-form__input--error': errors.name }"
          :placeholder="$t('design.designNamePlaceholder')"
          maxlength="100"
          @blur="validateName"
        />
        <p v-if="errors.name" class="design-form__error">{{ errors.name }}</p>
      </div>

      <!-- General error display -->
      <div v-if="errors.general" class="design-form__general-error">
        <svg viewBox="0 0 24 24" width="16" height="16">
          <path fill="currentColor" d="M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z"/>
        </svg>
        {{ errors.general }}
      </div>
    </div>

    <template #footer>
      <div class="design-form__actions">
        <AppButton
          @click="$emit('close')"
          variant="secondary"
        >
          {{ $t('common.cancel') }}
        </AppButton>
        <AppButton
          @click="handleSubmit"
          variant="primary"
          :loading="saving"
          :disabled="saving || !isFormValid"
        >
          {{ saving ? $t('common.saving') : $t('design.createDesign') }}
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, computed } from 'vue';
// @ts-ignore
import { watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AppModal from '../../base/Modal.vue';
import AppButton from '../../base/AppButton.vue';
import DesignService from '@/services/design.service';
import type { DesignCreationResponse } from '@/services/design.service';

interface Props {
  show: boolean;
  projectId: string | number;
}

interface Emits {
  (e: 'close'): void;
  (e: 'created', design: DesignCreationResponse): void;
}

const { t } = useI18n();

const props = withDefaults(defineProps<Props>(), {
  show: false
});

const emit = defineEmits<Emits>();

// Form data
const formData = ref({
  name: ''
});

// Form state
const saving = ref<boolean>(false);
const errors = ref<Record<string, string>>({});

// Computed properties
const isFormValid = computed(() => {
  return formData.value.name?.trim() &&
         Object.keys(errors.value).length === 0;
});

// Methods
const validateName = (): void => {
  if (errors.value.name) {
    validateForm();
  }
};

const validateForm = (): boolean => {
  const newErrors: Record<string, string> = {};

  // Name validation
  if (!formData.value.name?.trim()) {
    newErrors.name = t('design.nameRequired');
  } else if (formData.value.name.trim().length < 2) {
    newErrors.name = t('design.nameMinLength');
  } else if (formData.value.name.trim().length > 100) {
    newErrors.name = t('design.nameMaxLength');
  }

  errors.value = newErrors;
  return Object.keys(newErrors).length === 0;
};

const resetForm = (): void => {
  formData.value = {
    name: ''
  };
  errors.value = {};
};

const handleSubmit = async (): Promise<void> => {
  // Prevent duplicate submissions
  if (saving.value) {
    return;
  }

  if (!validateForm()) {
    return;
  }

  try {
    saving.value = true;

    const createdDesign = await DesignService.createDesign(props.projectId.toString(), formData.value.name.trim());

    emit('created', createdDesign);
    resetForm();

  } catch (error: any) {
    console.error('Error creating design:', error);

    // Handle specific backend errors
    if (error.response && error.response.data) {
      const responseData = error.response.data;

      // Handle business errors (like name already exists)
      if (responseData.businessError === 'DESIGN_NAME_ALREADY_EXISTS') {
        errors.value.name = t('design.nameExists');
      } else if (responseData.fieldErrors) {
        // Handle validation errors from server
        const serverErrors: Record<string, string> = {};
        responseData.fieldErrors.forEach((fieldError: any) => {
          serverErrors[fieldError.field] = fieldError.message;
        });
        errors.value = { ...errors.value, ...serverErrors };
      } else {
        // Generic server error
        errors.value.general = responseData.message || t('design.createError');
      }
    } else {
      // Network or other errors
      errors.value.general = error.message || t('design.createError');
    }
  } finally {
    saving.value = false;
  }
};

// Watch for modal visibility to reset form
watch(() => props.show, (newShow: boolean) => {
  if (newShow) {
    resetForm();
  }
});

// Real-time validation
watch(() => formData.value.name, () => {
  if (errors.value.name || errors.value.general) {
    // Clear general errors when user starts typing
    delete errors.value.general;

    // Re-validate name if there was an error
    if (errors.value.name) {
      validateForm();
    }
  }
});
</script>

<style scoped>
/* ==========================================================================
   DESIGN FORM STYLES
   ========================================================================== */

.design-form {
  padding: var(--space-md, 1rem) 0;
}

.design-form__group {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm, 0.5rem);
  margin-bottom: var(--space-lg, 1.5rem);
}

.design-form__label {
  color: var(--text-primary, #212529);
  font-weight: 500;
  font-size: var(--font-size-sm, 0.875rem);
  line-height: 1.5;
}

.design-form__required {
  color: var(--error, #dc3545);
  margin-left: var(--space-xs, 0.25rem);
}

.design-form__input {
  padding: 0.75rem;
  border: 1px solid var(--border-medium, #dee2e6);
  border-radius: var(--radius-md, 8px);
  font-size: var(--font-size-base, 1rem);
  width: 100%;
  background-color: var(--surface-primary, white);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  font-family: inherit;
}

.design-form__input:focus {
  border-color: var(--primary, #007bff);
  outline: none;
  box-shadow: 0 0 0 2px var(--primary-100, rgba(0, 123, 255, 0.25));
}

.design-form__input--error {
  border-color: var(--error, #dc3545);
}

.design-form__input--error:focus {
  border-color: var(--error, #dc3545);
  box-shadow: 0 0 0 2px var(--error-100, rgba(220, 53, 69, 0.25));
}

.design-form__error {
  display: flex;
  align-items: center;
  gap: var(--space-xs, 0.25rem);
  color: var(--error, #dc3545);
  font-size: var(--font-size-sm, 0.875rem);
  margin-top: var(--space-xs, 0.25rem);
}

.design-form__general-error {
  display: flex;
  align-items: center;
  gap: var(--space-sm, 0.5rem);
  padding: var(--space-md, 1rem);
  background-color: var(--error-50, #fef2f2);
  border: 1px solid var(--error-200, #fecaca);
  border-radius: var(--radius-md, 8px);
  color: var(--error-800, #991b1b);
  font-size: var(--font-size-sm, 0.875rem);
  margin-bottom: var(--space-lg, 1.5rem);
}

.design-form__general-error svg {
  flex-shrink: 0;
}

.design-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-md, 1rem);
}

/* Responsive design */
@media (max-width: 640px) {
  .design-form__actions {
    flex-direction: column-reverse;
    gap: var(--space-sm, 0.5rem);
  }
  
  .design-form__actions .app-button {
    width: 100%;
  }
}
</style>
